<template>
  <b-container fluid class="grm-body drt-dataset-panel">
    <b-row v-if="releases.length === 0" class="justify-content-center">
      <b-col cols="12" class="text-center grm-empty">
        <div class="grm-empty-icon">◎</div>
        <p>No releases available</p>
      </b-col>
    </b-row>

    <b-row class="grm-grid">
      <b-col
        v-for="(rel, index) in releases"
        :key="index"
        cols="6"
        class="grm-card-col mb-3"
      >
        <div
          class="grm-card"
          @click="datasetSelected(rel.id, rel.part_number, rel.extent_description, rel.release_date, rel.extent_id)"
        >
          <div class="grm-map-preview">
            <div class="grm-map-glow"></div>
            <img
              v-if="getRegionImage(rel.extent_description)"
              :src="getRegionImage(rel.extent_description)"
              :alt="rel.extent_description"
              class="grm-map-img"
            />
            <div v-else class="grm-map-placeholder">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="grm-globe-icon">
                <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
              </svg>
            </div>
          </div>

          <div class="grm-card-info">
            <div class="grm-region-name">{{ rel.extent_description }}</div>
            <div class="grm-release-label" v-if="rel.release_label">{{ rel.release_label }}</div>
            <div class="grm-release-date">
              <span class="grm-date-dot"></span>
              {{ formatDate(rel.release_date) }}
            </div>
          </div>

          <div class="grm-card-action">
            <span class="grm-action-label">Select</span>
            <svg class="grm-action-arrow" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <div class="grm-card-shine"></div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<style>
/* Two-column card grid: cards wrap into rows of two. */
.drt-dataset-panel .grm-grid {
  flex-direction: row;
  flex-wrap: wrap;
}

/* Slightly smaller preview so the region name + date get more of the
   half-width column. */
.drt-dataset-panel .grm-map-preview {
  width: 92px;
  min-width: 92px;
  height: 80px;
}

.drt-dataset-panel .grm-card-info {
  padding: 0 12px;
}

.drt-dataset-panel .grm-card-action {
  padding: 0 12px 0 4px;
}

/* Keep the release date on a single line. */
.drt-dataset-panel .grm-release-date {
  white-space: nowrap;
}
</style>

<script>
import axios from 'axios'
import {get as projGet, transform, transformExtent} from 'ol/proj'
import View from 'ol/View'
import australiaImg from '../../../assets/extents/Australia.svg'
import europeImg from '../../../assets/extents/Europe.svg'
import japanImg from '../../../assets/extents/Japan.svg'
import middleEastImg from '../../../assets/extents/MiddleEast.svg'
import newZealandImg from '../../../assets/extents/NewZealand.svg'
import northAmericaImg from '../../../assets/extents/NorthAmerica.svg'
import southKoreaImg from '../../../assets/extents/SouthKorea.svg'

const REGION_MAP = [
  { keys: ['australia'],                                                img: australiaImg },
  { keys: ['europe'],                                                   img: europeImg },
  { keys: ['japan'],                                                    img: japanImg },
  { keys: ['middle east', 'middleeast'],                               img: middleEastImg },
  { keys: ['new zealand', 'newzealand'],                               img: newZealandImg },
  { keys: ['north america', 'northamerica', 'usa', 'united states'],  img: northAmericaImg },
  { keys: ['south korea', 'southkorea', 'korea'],                     img: southKoreaImg },
]

export default {
  name: 'MMP_DRT_DatasetPanel',
  props: ['currentExtent', 'isModal', 'releases', 'selectedDataset', 'user'],
  data() {
    return {
      dataset: null,
      extents: [],
      zoomlevel: null
    }
  },
  methods: {
    getRegionImage(extentDescription) {
      if (!extentDescription) return null
      const lower = extentDescription.toLowerCase()
      for (const entry of REGION_MAP) {
        if (entry.keys.some(k => lower.includes(k))) return entry.img
      }
      return null
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },
    goBack() {
      this.$bvModal.hide('datasetSelection')
      this.$emit('returnHome')
    },
    getExtents() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/users/getExtents', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              company_id: this.user.org
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('getExtents API Call Error: ' + content.errors)
            } else if (content.length) {
              this.extents = []
              this.zoomlevel = content[0].zoomlevel
              content.forEach(element => {
                this.extents.push(element)
              })
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    changeExtent(selectedExtent) {
      let extentValues = []
      let extentCenter = []
      selectedExtent.extent.split(',').forEach(element => {
        extentValues.push(element)
      })
      selectedExtent.extent_center.split(',').forEach(element => {
        extentCenter.push(element)
      })
      let coord = new transformExtent(
        extentValues,
        new projGet('EPSG:4326'),
        new projGet('EPSG:3857')
      )
      let center = new transform(
        extentCenter,
        new projGet('EPSG:4326'),
        new projGet('EPSG:3857')
      )
      let view = new View({
        extent: coord,
        center: center,
        constrainOnlyCenter: true,
        zoom: this.zoomlevel,
        minZoom: 3,
        multiWorld: true
      })
      this.$emit('extentChanged', {
        changedViaDatasetSelection: true,
        selectedExtent: selectedExtent.extent_description,
        view: view
      })
    },
    datasetSelected(id, notes, extent, releaseDate, extent_id) {
      let dataset = {
        extent: extent,
        id: id,
        partNumber: notes,
        releaseDate: releaseDate,
        extent_id: extent_id
      }
      if (extent !== this.currentExtent) {
        let selectedExtent = this.extents.filter(
          e => e.extent_description == extent
        )[0]
        this.changeExtent(selectedExtent)
      }
      this.$emit('selection')
      this.$emit('setSelectedDataset', dataset)
      this.$emit('setShowDRTLayers')
      this.$emit('updateDRTLayersPartNumber', id)
      this.$bvModal.hide('datasetSelection')
    }
  },
  watch: {
    selectedDataset: function (newValue, oldValue) {
      this.dataset = newValue
    }
  },
  mounted() {
    this.getExtents()
  }
}
</script>
